import React, { useEffect, useState, useRef } from "react";
import SVG from "react-inlinesvg";
import { sendAppEvent } from "../../../Utils";
import { useProjectStore } from "../../../stores/projectStore";
import { createMenuInteractionEvent } from "src/events/MenuInteractionEvent";
import { useToast } from "../../ToastWrapper";
import { SudoMenuButton, MenuPopoverItem } from "src/sudokit/components/SudoMenuButton";
import { MenuDeleteConfirmation } from "src/sudokit/components/SudoMenuButton/MenuDeleteConfirmation";
import Project from "../../../models/Project";

interface DocumentItem {
  id: string;
  title: string;
  type: "document";
  name?: string;
}

interface FolderItem {
  id: string;
  name: string;
  type: "folder";
  title?: string;
}

type ListItem = DocumentItem | FolderItem;

interface ActionConfig {
  label: string;
  icon?: React.ReactNode;
  handler?: () => void | Promise<void>;
  disabled?: boolean;
  suppressGenericError?: boolean;
}

interface DocumentListActionMenuProps {
  item: ListItem;
  setNewTitle: (title: string) => void;
  onOpenChange?: (open: boolean) => void;
}

export const DocumentListActionMenu = ({ item, setNewTitle, onOpenChange }: DocumentListActionMenuProps) => {
  const project = useProjectStore((state: any) => state.project);
  const contents = useProjectStore((state: any) => state.contents);
  const setEditingTitle = useProjectStore((state: any) => state.setEditingTitle);
  const setDocumentId = useProjectStore((state: any) => state.setDocumentId);
  const documentId = useProjectStore((state: any) => state.document?.id);
  const isDeletingDocumentFolder = useProjectStore((state: any) => state.isDeletingDocumentFolder);
  const setIsDeletingDocumentFolder = useProjectStore((state: any) => state.setIsDeletingDocumentFolder);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState("");
  const [isExportingDocument, setIsExportingDocument] = useState(false);
  const [isExportingFolder, setIsExportingFolder] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { setToast } = useToast();

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(""), 4000);
    return () => clearTimeout(timeout);
  }, [error]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    if (!open) {
      setShowDeleteConfirm(false);
    }
    if (open && triggerRef.current) {
      const event = createMenuInteractionEvent();
      triggerRef.current.dispatchEvent(event);
    }
  };

  const confirmDeleteDocument = () => {
    if (isDeletingDocumentFolder) {
      setToast("Moving items to trash. Please wait until the process is complete.");
      return;
    }
    setIsDeletingDocumentFolder(true);
    const deletePromise = project.deleteDocument(item.id);
    if (documentId === item.id) {
      setDocumentId(project.contents[0].id);
    }
    sendAppEvent("Document Deleted", { position: "Document Action Menu" });
    deletePromise
      .catch(() => setToast("Failed to move document to trash. Please try again."))
      .finally(() => setIsDeletingDocumentFolder(false));
  };

  const confirmDeleteFolder = () => {
    if (isDeletingDocumentFolder) {
      setToast("Moving items to trash. Please wait until the process is complete.");
      return;
    }
    setIsDeletingDocumentFolder(true);
    const deletePromise = project.deleteFolder(item.id);
    sendAppEvent("Folder Deleted", { position: "Folder Action Menu" });
    deletePromise
      .catch(() => setToast("Failed to move folder to trash. Please try again."))
      .finally(() => setIsDeletingDocumentFolder(false));
  };

  const documentActions: Record<string, ActionConfig> = {
    rename: {
      label: "Rename",
      handler: () => {
        setEditingTitle(item.id);
        setNewTitle(item.title ?? "");
        sendAppEvent("Document Renamed", { position: "Document Action Menu" });
      },
      icon: <SVG src="/assets/pencil.svg" />,
    },
    duplicate: {
      label: "Duplicate",
      icon: <SVG src="/assets/duplicate.svg" />,
      handler: async () => {
        const res = await project.duplicateDocument(item.id);
        const { duplicated } = res;
        setDocumentId(duplicated.id);
        sendAppEvent("Document Duplicated", { position: "Document Action Menu" });
      },
    },
    export: {
      label: isExportingDocument ? "Exporting..." : "Export (.docx)",
      icon: <SVG src="/assets/export.svg" />,
      disabled: isExportingDocument,
      suppressGenericError: true,
      handler: async () => {
        if (isExportingDocument) {
          return;
        }

        const documentTitle = item?.title || "document";
        setIsExportingDocument(true);
        setToast(`Preparing "${documentTitle}" for download...`, {
          dismissible: false,
          durationMs: Infinity,
        });

        try {
          await project.exportDocument(item.id, item.title);
          sendAppEvent("Document Exported", { position: "Document Action Menu" });
          setToast(`Finished exporting "${documentTitle}".`);
          setError("");
        } catch (exportError: unknown) {
          const message = exportError instanceof Error ? exportError.message : "Failed to export document. Please try again later.";
          setError(message);
          setToast(message);
        } finally {
          setIsExportingDocument(false);
        }
      },
    },
    delete: {
      label: "Delete",
      icon: <SVG src="/assets/delete.svg" />,
    },
  };

  const folderActions: Record<string, ActionConfig> = {
    rename: {
      label: "Rename",
      handler: () => {
        setEditingTitle(item.id);
        setNewTitle(item.name ?? "");
        sendAppEvent("Folder Renamed", { position: "Folder Action Menu" });
      },
      icon: <SVG src="/assets/pencil.svg" />,
    },
    exportZip: {
      label: isExportingFolder ? "Exporting..." : (() => {
        const docCount = project.getDocumentCountInFolder(item.id);
        return docCount ? `Export as ${docCount} docs` : "Export (.docx zip)";
      })(),
      icon: <SVG src="/assets/export.svg" />,
      disabled: isExportingFolder,
      suppressGenericError: true,
      handler: async () => {
        if (isExportingFolder) return;
        const folderName = item?.name || "folder";
        setIsExportingFolder(true);
        setToast(`Preparing "${folderName}" for download...`, { dismissible: false, durationMs: Infinity });
        try {
          await Project.exportFolder(project.id, item.id, folderName);
          sendAppEvent("Folder Exported", { position: "Folder Action Menu", mode: "zip" });
          setToast(`Finished exporting "${folderName}".`);
        } catch (exportError: unknown) {
          const message = exportError instanceof Error ? exportError.message : "Failed to export folder.";
          setToast(message);
        } finally {
          setIsExportingFolder(false);
        }
      },
    },
    exportMerged: {
      label: isExportingFolder ? "Exporting..." : "Export as 1 doc",
      icon: <SVG src="/assets/export.svg" />,
      disabled: isExportingFolder,
      suppressGenericError: true,
      handler: async () => {
        if (isExportingFolder) return;
        const folderName = item?.name || "folder";
        setIsExportingFolder(true);
        setToast(`Preparing "${folderName}" merged export for download...`, { dismissible: false, durationMs: Infinity });
        try {
          await Project.exportMergedFolder(project.id, item.id, folderName);
          sendAppEvent("Folder Exported", { position: "Folder Action Menu", mode: "merged" });
          setToast(`Finished exporting "${folderName}".`);
        } catch (exportError: unknown) {
          const message = exportError instanceof Error ? exportError.message : "Failed to export folder.";
          setToast(message);
        } finally {
          setIsExportingFolder(false);
        }
      },
    },
    delete: {
      label: "Delete",
      icon: <SVG src="/assets/delete.svg" />,
    },
  };

  const actions = item.type === "document" ? documentActions : folderActions;
  const confirmationMessage =
    item.type === "document" ? <div>This will move the document to your project&nbsp;trash.</div> : <div>This will move all the folder's contents to your project&nbsp;trash.</div>;

  if (contents.length === 1) {
    delete actions.delete;
  }

  const handleAction = async (actionKey: string) => {
    const actionConfig = actions[actionKey];
    if (!actionConfig?.handler) return;

    try {
      await actionConfig.handler();
    } catch (err: unknown) {
      if (!actionConfig.suppressGenericError) {
        const fallbackMessage = err instanceof Error ? err.message : "Something went wrong. Please try again later.";
        setError(fallbackMessage);
        setToast(fallbackMessage);
      }
    }
  };

  return (
    <>
      <SudoMenuButton
        ref={triggerRef}
        onOpenChange={handleOpenChange}
        trigger={
          <button
            className="item-menu"
            aria-label={`Actions for ${item.type === "document" ? "document" : "folder"} "${item.title || item.name || "Untitled"}"`}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ border: "none", background: "none", padding: 4, borderRadius: 4, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <span className="icon-wrapper hotdog-dots" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <SVG src="/assets/hotdog-menu.svg" width={18} height={18} />
            </span>
          </button>
        }
        side="bottom"
        align="end"
        sideOffset={4}
        collisionPadding={8}
        menuAriaLabel={`Actions for ${item.type === "document" ? "document" : "folder"} "${item.title || item.name || "Untitled"}"`}
      >
        {!showDeleteConfirm ? (
          <>
            {Object.entries(actions).map(([key, action]) => (
              <MenuPopoverItem
                key={key}
                variant={key === "delete" ? "destructive" : "default"}
                disabled={action.disabled}
                closeOnSelect={key === "delete" ? false : true}
                onSelect={() => key === "delete" ? setShowDeleteConfirm(true) : handleAction(key)}
                className="whitespace-nowrap"
              >
                <span aria-hidden="true">{action.icon}</span>
                <span>{action.label}</span>
              </MenuPopoverItem>
            ))}
          </>
        ) : (
          <MenuDeleteConfirmation
            width={item.type === "document" ? 168 : 200}
            onConfirm={() => item.type === "document" ? confirmDeleteDocument() : confirmDeleteFolder()}
          >
            {confirmationMessage}
          </MenuDeleteConfirmation>
        )}
      </SudoMenuButton>
      {error && <div id="error">{error}</div>}
    </>
  );
};
